import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  ArrowLeft, Loader2, KeyRound, Mail, CheckCircle2, XCircle, FileText, Receipt, Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import { MemberStatusBadge } from './MembershipAccounts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtAUD = (n) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n || 0);
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const PayStatusBadge = ({ status }) => {
  const map = {
    paid: ['bg-emerald-100 text-emerald-700', 'Paid'],
    receipt_uploaded: ['bg-blue-100 text-blue-700', 'Receipt uploaded'],
    unpaid: ['bg-rose-100 text-rose-700', 'Unpaid'],
  };
  const [cls, label] = map[status] || ['bg-slate-100 text-slate-600', status];
  return <Badge className={`${cls} hover:${cls}`}>{label}</Badge>;
};

export default function MemberAccountDetails() {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [member, setMember] = useState(null);
  const [payments, setPayments] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreds, setShowCreds] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' });
        if (!r.ok) { navigate('/login'); return; }
        const u = await r.json();
        if (u.role !== 'super_admin' && u.role !== 'admin') { toast.error('Owner access only'); navigate('/'); return; }
        setUser(u);
      } catch { navigate('/login'); }
    })();
  }, [navigate]);

  const refresh = async () => {
    setLoading(true);
    try {
      const [mRes, pRes, tRes, iRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/memberships/${memberId}`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/memberships/${memberId}/payments`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/memberships/${memberId}/transactions`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/memberships/${memberId}/invoices`, { credentials: 'include' }),
      ]);
      if (!mRes.ok) { toast.error('Member not found'); navigate('/accounts'); return; }
      setMember(await mRes.json());
      if (pRes.ok) setPayments(await pRes.json());
      if (tRes.ok) setTransactions(await tRes.json());
      if (iRes.ok) setInvoices(await iRes.json());
    } catch { toast.error('Failed to load member'); }
    finally { setLoading(false); }
  };
   
  useEffect(() => { if (user) refresh(); }, [user, memberId]);

  const sendPaymentRequest = async () => {
    setSendingEmail(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/memberships/${memberId}/request-payment`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.email_sent ? 'Payment request email sent' : 'Payment record created (email could not be sent)');
      refresh();
    } catch (e) { toast.error(e.message); }
    finally { setSendingEmail(false); }
  };

  const actOnPayment = async (paymentId, action) => {
    try {
      const body = action === 'reject' ? JSON.stringify({ reason: 'Receipt rejected by admin' }) : undefined;
      const r = await fetch(`${BACKEND_URL}/api/memberships/payments/${paymentId}/${action}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.message || 'Done');
      refresh();
    } catch (e) { toast.error(e.message); }
  };

  const viewReceipt = async (paymentId) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/memberships/payments/${paymentId}/receipt`, { credentials: 'include' });
      if (!r.ok) throw new Error('Receipt not available');
      const blob = await r.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (e) { toast.error(e.message); }
  };

  if (!user) return null;

  return (
    <Layout user={user}>
      <div className="p-4 md:p-6 lg:p-8" data-testid="member-details-page">
        <Button variant="ghost" size="sm" onClick={() => navigate('/accounts')} className="mb-3" data-testid="member-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Accounts
        </Button>

        {loading || !member ? (
          <div className="py-16 text-center text-slate-500"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>
        ) : (
          <>
            <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="font-['Chivo'] font-bold text-2xl sm:text-3xl text-slate-900">
                    {member.company_name || `${member.first_name} ${member.last_name || ''}`}
                  </h1>
                  <MemberStatusBadge status={member.status} />
                </div>
                <p className="text-slate-500 text-sm mt-1">
                  {member.first_name} {member.last_name || ''} · {member.mobile} · {member.email}
                  {member.abn ? ` · ABN ${member.abn}` : ''}
                </p>
                <p className="text-slate-400 text-xs mt-0.5">
                  Member since {fmtDate(member.member_since)} · Monthly {member.monthly_amount ? fmtAUD(member.monthly_amount) : '—'}
                  {member.has_portal_credentials ? ' · Portal login enabled' : ' · No portal login yet'}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" onClick={() => setShowEdit(true)} data-testid="edit-member-btn">
                  <Pencil className="h-4 w-4 mr-2" /> Edit
                </Button>
                <Button variant="outline" onClick={() => setShowCreds(true)} data-testid="set-credentials-btn">
                  <KeyRound className="h-4 w-4 mr-2" /> {member.has_portal_credentials ? 'Reset portal password' : 'Create portal login'}
                </Button>
                <Button onClick={sendPaymentRequest} disabled={sendingEmail || member.status !== 'active'}
                  className="bg-slate-900 hover:bg-slate-800" data-testid="send-payment-request-btn">
                  {sendingEmail ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                  Email payment request
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-6">
              <Card className="p-4" data-testid="member-kpi-accumulated">
                <p className="text-xs uppercase tracking-wide text-slate-500">Accumulated (paid subscriptions)</p>
                <p className="text-2xl font-semibold mt-1 text-emerald-600">{fmtAUD(member.accumulated)}</p>
              </Card>
              <Card className="p-4" data-testid="member-kpi-used">
                <p className="text-xs uppercase tracking-wide text-slate-500">Used (invoices)</p>
                <p className="text-2xl font-semibold mt-1 text-slate-900">{fmtAUD(member.used)}</p>
              </Card>
              <Card className="p-4" data-testid="member-kpi-balance">
                <p className="text-xs uppercase tracking-wide text-slate-500">Balance</p>
                <p className={`text-2xl font-semibold mt-1 ${member.balance < 0 ? 'text-rose-600' : 'text-blue-600'}`}>{fmtAUD(member.balance)}</p>
              </Card>
            </div>

            {/* Monthly subscriptions */}
            <Card className="p-5 mb-6" data-testid="member-subscriptions-section">
              <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Receipt className="h-4 w-4 text-slate-400" /> Monthly Subscriptions
              </h2>
              {payments.length === 0 ? (
                <p className="text-sm text-slate-500 py-4">No subscription months yet. The statement email goes out automatically on the 30th — or use "Email payment request" to start this month now.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Period</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      <TableHead>Receipt</TableHead>
                      <TableHead>Emails</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((p) => (
                      <TableRow key={p.payment_id} data-testid={`payment-row-${p.period}`}>
                        <TableCell className="font-medium">{p.period_label || p.period}</TableCell>
                        <TableCell className="text-right">{fmtAUD(p.amount_due)}</TableCell>
                        <TableCell className="text-center"><PayStatusBadge status={p.status} /></TableCell>
                        <TableCell>
                          {p.has_receipt ? (
                            <button className="text-blue-600 underline text-sm" onClick={() => viewReceipt(p.payment_id)} data-testid={`view-receipt-${p.period}`}>
                              View receipt
                            </button>
                          ) : p.rejected_reason && p.status === 'unpaid' ? (
                            <span className="text-xs text-rose-500">Rejected — awaiting re-upload</span>
                          ) : (
                            <span className="text-xs text-slate-400">Not uploaded</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-slate-500">
                          {p.statement_email_sent_at ? `Sent ${fmtDate(p.statement_email_sent_at)}` : 'Not emailed'}
                          {p.reminder_count > 0 && ` · ${p.reminder_count} reminder${p.reminder_count > 1 ? 's' : ''}`}
                        </TableCell>
                        <TableCell className="text-right">
                          {p.status === 'receipt_uploaded' && (
                            <div className="flex justify-end gap-2">
                              <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" onClick={() => actOnPayment(p.payment_id, 'approve')} data-testid={`approve-payment-${p.period}`}>
                                <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve
                              </Button>
                              <Button size="sm" variant="outline" className="h-8 text-rose-600 border-rose-200" onClick={() => actOnPayment(p.payment_id, 'reject')} data-testid={`reject-payment-${p.period}`}>
                                <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
                              </Button>
                            </div>
                          )}
                          {p.status === 'unpaid' && (
                            <Button size="sm" variant="outline" className="h-8" onClick={() => actOnPayment(p.payment_id, 'approve')} data-testid={`mark-paid-${p.period}`}
                              title="Mark paid manually (e.g. paid in person)">
                              Mark paid
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {/* Transaction statement */}
              <Card className="p-5" data-testid="member-transactions-section">
                <h2 className="font-semibold text-slate-900 mb-3">Transaction Statement</h2>
                {transactions.length === 0 ? (
                  <p className="text-sm text-slate-500 py-4">No transactions yet — approved subscription payments and invoices will appear here.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Date</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Credit</TableHead>
                        <TableHead className="text-right">Debit</TableHead>
                        <TableHead className="text-right">Balance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactions.map((t, i) => (
                        <TableRow key={i} data-testid="transaction-row">
                          <TableCell className="text-xs text-slate-500 whitespace-nowrap">{fmtDate(t.date)}</TableCell>
                          <TableCell className="text-sm">{t.description}</TableCell>
                          <TableCell className="text-right text-emerald-600">{t.credit ? fmtAUD(t.credit) : '—'}</TableCell>
                          <TableCell className="text-right text-slate-700">{t.debit ? fmtAUD(t.debit) : '—'}</TableCell>
                          <TableCell className={`text-right font-medium ${t.running_balance < 0 ? 'text-rose-600' : 'text-slate-900'}`}>{fmtAUD(t.running_balance)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Card>

              {/* Invoices */}
              <Card className="p-5" data-testid="member-invoices-section">
                <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-slate-400" /> Invoices
                </h2>
                {invoices.length === 0 ? (
                  <p className="text-sm text-slate-500 py-4">No invoices since membership started.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Invoice</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Vehicle</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-center">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoices.map((inv) => (
                        <TableRow key={inv.invoice_id} className="cursor-pointer hover:bg-slate-50"
                          onClick={() => navigate(`/invoices/${inv.invoice_id}`)} data-testid="member-invoice-row">
                          <TableCell className="font-mono text-sm">{inv.doc_no || inv.invoice_id}</TableCell>
                          <TableCell className="text-sm">{fmtDate(inv.invoice_date || inv.created_at)}</TableCell>
                          <TableCell className="text-sm">{inv.vehicle_rego || '—'}</TableCell>
                          <TableCell className="text-right font-medium">{fmtAUD(inv.total)}</TableCell>
                          <TableCell className="text-center"><Badge variant="outline">{inv.status}</Badge></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Card>
            </div>
          </>
        )}

        {member && (
          <>
            <CredentialsDialog open={showCreds} memberId={memberId} onClose={() => setShowCreds(false)} onDone={refresh} />
            <EditMemberDialog open={showEdit} member={member} onClose={() => setShowEdit(false)} onDone={refresh} />
          </>
        )}
      </div>
    </Layout>
  );
}

function CredentialsDialog({ open, memberId, onClose, onDone }) {
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const close = () => { setPassword(''); onClose(); };

  const submit = async () => {
    if (password.length < 6) return toast.error('Password must be at least 6 characters');
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/memberships/${memberId}/credentials`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success('Portal password set — share it with the member');
      onDone(); close();
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-sm" data-testid="credentials-dialog">
        <DialogHeader><DialogTitle>Member portal login</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">The member logs in with their <strong>email</strong> and this password at <span className="font-mono">/member-portal</span>.</p>
        <div>
          <Label>Password</Label>
          <Input type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" data-testid="credentials-password-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="bg-slate-900 hover:bg-slate-800" data-testid="credentials-submit-btn">
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save password
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditMemberDialog({ open, member, onClose, onDone }) {
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    if (open && member) {
      setForm({
        company_name: member.company_name || '', first_name: member.first_name || '',
        last_name: member.last_name || '', mobile: member.mobile || '', email: member.email || '',
        license_number: member.license_number || '', abn: member.abn || '', address: member.address || '',
        monthly_amount: member.monthly_amount || '', status: member.status,
      });
    }
  }, [open, member]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        company_name: form.company_name || null, first_name: form.first_name || null,
        last_name: form.last_name || null, mobile: form.mobile || null, email: form.email || null,
        license_number: form.license_number || null, abn: form.abn || null, address: form.address || null,
        monthly_amount: form.monthly_amount ? parseFloat(form.monthly_amount) : null,
        status: form.status || null,
      };
      const r = await fetch(`${BACKEND_URL}/api/memberships/${member.member_id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success('Member updated');
      onDone(); onClose();
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-member-dialog">
        <DialogHeader><DialogTitle>Edit member</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Company name</Label><Input value={form.company_name || ''} onChange={set('company_name')} /></div>
          <div><Label>First name</Label><Input value={form.first_name || ''} onChange={set('first_name')} /></div>
          <div><Label>Last name</Label><Input value={form.last_name || ''} onChange={set('last_name')} /></div>
          <div><Label>Mobile</Label><Input value={form.mobile || ''} onChange={set('mobile')} /></div>
          <div><Label>Email</Label><Input value={form.email || ''} onChange={set('email')} /></div>
          <div><Label>Licence number</Label><Input value={form.license_number || ''} onChange={set('license_number')} /></div>
          <div><Label>ABN</Label><Input value={form.abn || ''} onChange={set('abn')} /></div>
          <div className="col-span-2"><Label>Address</Label><Input value={form.address || ''} onChange={set('address')} /></div>
          <div>
            <Label>Monthly subscription (AUD)</Label>
            <Input type="number" min="1" value={form.monthly_amount || ''} onChange={set('monthly_amount')} data-testid="edit-monthly-input" />
          </div>
          <div>
            <Label>Status</Label>
            <select className="w-full h-10 border rounded-md px-3 text-sm" value={form.status || 'active'}
              onChange={set('status')} data-testid="edit-status-select">
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="bg-slate-900 hover:bg-slate-800" data-testid="edit-member-submit">
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
